import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import { addEventsData, getEventsDataById, updateEventsDataById, getAllDataWithFlag } from '../../../../services/CommonService';
import { imgURL, Main_Banner_WIDTH, Main_Banner_HEIGHT, Main_Banner_MAXSIZE_MB } from '../../../Config';
import TipTapEditor from '../../../../Utils/TipTapEditor';

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [bannerPreview, setBannerPreview] = useState(null);
  const [projectData, setProjectData] = useState([]);
  const [lookupData, setLookupData] = useState([]);
  const [themes, setThemes] = useState([]);
  const isEdit = id !== 'new';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_end_date: '',
    event_time: '',
    entry_type: '2',
    theme: '',
    subtheme: '',
    event_end_time: '',
    number_of_participants: '',
    evidence: '',
    sponsors: '',
    key_highlights: '',
    project_id: '',
    mode: '1',
    location_link: '',
    venue: '',
    meeting_place: '',
    organisation: '',
    host: '',
    agenda_document: null,
    banner: null,
    organization_details: [{ name: '', organization: '' }],
    email_sign: '',
    prerequistes: ''
  });

  useEffect(() => {
    getProjectData();
    if (isEdit) {
      fetchEventDetails();
    };
    const lookup = JSON.parse(localStorage.getItem('lookup'));
    setLookupData(lookup);
    const theme = JSON.parse(localStorage.getItem('themeData'));
    setThemes(theme);
  }, [id]);

  const getProjectData = async () => {
    try {
      const response = await getAllDataWithFlag(5);
      setProjectData(response?.data?.project)
    } catch (err) {
      Swal.fire({
        title: "Error",
        icon: "error",
        text: err.message,
        confirmButtonText: "Ok",
      });
    }
  }

  const fetchEventDetails = async () => {
    try {
      const response = await getEventsDataById(id);
      setEvent(response.data);
      const mergedData = { ...formData, ...response.data };
      if (!response?.data?.organization_details?.length) {
        mergedData.organization_details = formData.organization_details;
      }
      setFormData(mergedData);
      if (response.data.banner) {
        setBannerPreview(response.data.banner);
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData({ ...formData, [name]: files[0] });
      if (name === 'banner') {
        const file = files[0];
        if (file) {
          if (file.size > Main_Banner_MAXSIZE_MB * 1024 * 1024) {
            setFormErrors((prev) => ({ ...prev, banner: `File size exceeds ${Main_Banner_MAXSIZE_MB} MB.` }));
            setFormData({ ...formData, [name]: null });
            return;
          }
          const reader = new FileReader();
          reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
              // Store dimensions on the file object for later validation in validateForm
              file.width = img.width;
              file.height = img.height;
              setFormData({ ...formData, [name]: file });
              setFormErrors((prev) => ({ ...prev, banner: "" })); // Clear previous image errors
            };
            img.onerror = () => {
              setFormErrors((prev) => ({ ...prev, banner: "Could not load image. Invalid image file." }));
              setFormData({ ...formData, [name]: null });
            };
            img.src = event.target.result;
          };
          reader.onloadend = () => {
            setBannerPreview(reader.result);
          };
          reader.readAsDataURL(file);
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSpeakerChange = (index, field, value) => {
    const updatedSpeakers = [...formData.organization_details];
    updatedSpeakers[index] = {
      ...updatedSpeakers[index],
      [field]: value
    };
    setFormData({ ...formData, organization_details: updatedSpeakers });
  };

  const addSpeaker = () => {
    setFormData({ ...formData, organization_details: [...formData.organization_details, { name: '', organization: '' }] });
  };

  const removeSpeaker = (index) => {
    const updatedSpeakers = formData.organization_details.filter((_, i) => i !== index);
    setFormData({ ...formData, organization_details: updatedSpeakers });
  };

  const isDateBefore = (d1, d2) =>
    moment(d1, "YYYY-MM-DD").isBefore(moment(d2, "YYYY-MM-DD"), "day");

  const isTimeBefore = (t1, t2) =>
    moment(t1, "HH:mm").isBefore(moment(t2, "HH:mm"));

  const validate = () => {
    const errors = {};
    if (!bannerPreview && !formData.banner) errors.banner = 'Banner is required.';
    if (!formData.title) errors.title = 'Event Name is required.';
    if (!formData.description) errors.description = 'Event Description is required.';
    if (!formData.event_date) errors.event_date = 'Start Date is required.';
    if (!formData.event_end_date) errors.event_end_date = 'End Date is required.';
    if (!formData.event_time) errors.event_time = 'Start Time is required.';
    if (!formData.event_end_time) errors.event_end_time = 'End Time is required.';
    if (!formData.prerequistes) errors.prerequistes = 'Prerequisites is required.';
    if (formData?.entry_type === '1' && !formData.ticket_price) errors.ticket_price = 'Ticket price is required.';
    if (!formData.mode) errors.mode = 'Event Type is required.';
    if (formData.mode === '2' && !formData.location_link) {
      errors.location_link = 'Virtual Link is required.';
    }
    if (isDateBefore(formData.event_end_date, formData.event_date)) {
      errors.event_end_date = "End date can’t be before start date.";
    }
    const sameDay = formData.event_date === formData.event_end_date;
    if (sameDay && isTimeBefore(formData.event_end_time, formData.event_time)) {
      errors.event_end_time = "End time can’t be before start time.";
    }

    if (bannerPreview) {
      if (formData?.banner?.width < Main_Banner_WIDTH || formData?.banner?.height < Main_Banner_HEIGHT) {
        errors.banner = `Image must be ${Main_Banner_WIDTH} x ${Main_Banner_HEIGHT} px. Current: ${formData?.banner?.width} x ${formData?.banner?.height} px.`;
      } else if (formData?.banner?.size > Main_Banner_MAXSIZE_MB * 1024 * 1024) {
        errors.banner = `File size exceeds ${Main_Banner_MAXSIZE_MB} MB.`
      }
    }
    if (formData.mode === '1' && !formData.venue) {
      errors.venue = 'Venue is required.';
    }
    if (formData.mode === '1' && !formData.meeting_place) {
      errors.meeting_place = 'Meeting Place is required.';
    }
    if (formData?.organization_details?.length > 0) {
      formData?.organization_details.forEach(element => {
        if (element?.name && !element?.organization) {
          errors.organization = 'Organisation is required with speakers.';
        };
        if (!element?.name && element?.organization) {
          errors.speakers = 'Speaker is required with organisation.';
        };
      });
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    try {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === 'organization_details') {
          const speakerNames = formData?.organization_details?.map(s => s?.name).join(',');
          const speakerOrgs = formData?.organization_details?.map(s => s?.organization).join(',');
          if (speakerNames) data.append('speakers', speakerNames);
          if (speakerOrgs) data.append('organisation', speakerOrgs);
        } else if (key === 'banner') {
          if (formData[key] && formData[key] instanceof File) data.append('banner', formData[key]);
        } else if (formData[key]) {
          data.append(key, formData[key]);
        };
      });

      if (isEdit) {
        await updateEventsDataById(data, id);
        Swal.fire('Success', 'Event updated successfully!', 'success');
      } else {
        await addEventsData(data);
        Swal.fire('Success', 'Event created successfully!', 'success');
      }
      navigate('../eventlist');
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || err?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card">
        <div className="card-header adminCardHeader d-flex justify-content-between align-items-center">
          <h4>{isEdit ? 'Edit Event' : 'Create Event'}</h4>
          <button type="button" className="btn btn-sm btn-warning me-2" onClick={() => navigate('../eventlist')}>
            <i className="bi bi-arrow-left me-1" /> Back
          </button>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <div className="row mb-3">
              <div className="col-md-8">
                <label className="form-label required">Banner ({Main_Banner_WIDTH} × {Main_Banner_HEIGHT} px, Max Size: {Main_Banner_MAXSIZE_MB} MB)</label>
                <input
                  type="file"
                  className={`form-control ${formErrors.banner ? 'is-invalid' : ''}`}
                  name="banner"
                  onChange={handleChange}
                />
                {formErrors.banner && <div className="invalid-feedback">{formErrors.banner}</div>}
              </div>
              {bannerPreview && (
                <div className="col-md-4">
                  <img src={bannerPreview?.startsWith('data:image') ? bannerPreview : `${imgURL}event_banners/${bannerPreview}`} alt="Banner Preview" style={{ width: '80px', height: '65px' }} />
                </div>
              )}
            </div>
            <hr /><br />
            <hr />
            <div className="mb-3">
              <label className="form-label required">Event Name</label>
              <input
                type="text"
                className={`form-control ${formErrors.title ? 'is-invalid' : ''}`}
                name="title"
                value={formData.title ?? ''}
                onChange={handleChange}
              />
              {formErrors.title && <div className="invalid-feedback">{formErrors.title}</div>}
            </div>

            <div className="col-md-12 mb-3">
              <label className="form-label required">Event Description</label>
              <TipTapEditor
                value={formData.description ?? ''}
                onChange={(content) => {
                  setFormData({ ...formData, description: content });
                  if (formErrors.description) {
                    setFormErrors((prev) => ({
                      ...prev,
                      description: "",
                    }));
                  }
                }}
                isGrayMode={false}
              />
              {formErrors.description && (
                <div className="text-danger mt-1">{formErrors.description}</div>
              )}
            </div>
            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label required">Start Date</label>
                <input
                  type="date"
                  className={`form-control ${formErrors.event_date ? 'is-invalid' : ''}`}
                  name="event_date"
                  value={moment(formData.event_date).format('YYYY-MM-DD')}
                  onChange={handleChange}
                />
                {formErrors.event_date && <div className="invalid-feedback">{formErrors.event_date}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label required">End Date</label>
                <input
                  type="date"
                  className={`form-control ${formErrors.event_end_date ? 'is-invalid' : ''}`}
                  name="event_end_date"
                  value={moment(formData.event_end_date).format('YYYY-MM-DD')}
                  onChange={handleChange}
                />
                {formErrors.event_end_date && <div className="invalid-feedback">{formErrors.event_end_date}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label required">Start Time</label>
                <input
                  type="time"
                  className={`form-control ${formErrors?.event_time ? 'is-invalid' : ''}`}
                  name="event_time"
                  value={formData.event_time}
                  onChange={handleChange}
                />
                {formErrors?.event_time && <div className="invalid-feedback">{formErrors?.event_time}</div>}
              </div>
              <div className="col-md-3">
                <label className="form-label required">End Time</label>
                <input
                  type="time"
                  className={`form-control ${formErrors?.event_end_time ? 'is-invalid' : ''}`}
                  name="event_end_time"
                  value={formData.event_end_time}
                  onChange={handleChange}
                />
                {formErrors?.event_end_time && <div className="invalid-feedback">{formErrors?.event_end_time}</div>}
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label required">Event Type</label>
                <select
                  className={`form-select ${formErrors?.mode ? 'is-invalid' : ''}`}
                  name="mode"
                  value={formData.mode ?? ''}
                  onChange={handleChange}
                ><option value="">Select Type</option>
                  {
                    lookupData?.filter((m) => m?.Flag === "10")?.map((m, i) => (
                      <option value={m?.LookupCode} key={i}>{m?.LookupValue}</option>
                    ))
                  }
                </select>
                {formErrors.mode && <div className="invalid-feedback">{formErrors.mode}</div>}
              </div>
              <div className="col-md-6">
                <label className={`form-label ${(formData?.mode === '2') ? 'required' : ''} `}>Virtual Link</label>
                <input
                  type="text"
                  className={`form-control ${formData?.mode === '2' && formErrors?.location_link ? 'is-invalid' : ''}`}
                  name="location_link"
                  value={formData.location_link ?? ''}
                  onChange={handleChange}
                  disabled={formData?.mode === '1'}
                />
                {formData?.mode === '2' && formErrors?.location_link && <div className="invalid-feedback">{formErrors.location_link}</div>}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className={`form-label ${(formData?.mode === '1') ? 'required' : ''}`}>Venue</label>
                <textarea
                  className={`form-control ${formData?.mode === '1' && formErrors?.venue ? 'is-invalid' : ''}`}
                  name="venue"
                  rows="3"
                  value={formData.venue ?? ''}
                  onChange={handleChange}
                  disabled={formData.mode === '2'}
                ></textarea>
                {formData?.mode === '1' && formErrors?.venue && <div className="invalid-feedback">{formErrors.venue}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label">Host</label>
                <textarea
                  className={`form-control`}
                  name="host"
                  rows="3"
                  value={formData.host ?? ''}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className={`form-label ${(formData?.mode === '1') ? 'required' : ''}`}>Meeting Place</label>
                <input
                  type="text"
                  className={`form-control ${formData?.mode === '1' && formErrors?.meeting_place ? 'is-invalid' : ''}`}
                  name="meeting_place"
                  value={formData.meeting_place ?? ''}
                  onChange={handleChange}
                  disabled={formData?.mode === '2'}
                />
                {formErrors?.meeting_place && <div className="invalid-feedback">{formErrors?.meeting_place}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label">Entry Type</label>
                <select
                  className={`form-select`}
                  name="entry_type"
                  value={formData.entry_type ?? ''}
                  onChange={handleChange}
                >
                  <option value="">Select Type</option>
                  {
                    lookupData?.filter((m) => m?.Flag === "8")?.map((m, i) => (
                      <option value={m?.LookupCode} key={i}>{m?.LookupValue}</option>
                    ))
                  }
                </select>
              </div>

            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className={`form-label ${(formData?.entry_type === '1') ? 'required' : ''} `}>Ticket Price</label>
                <input
                  type="text"
                  className={`form-control ${formData?.entry_type === '1' && formErrors.ticket_price ? 'is-invalid' : ''}`}
                  name="ticket_price"
                  value={formData.ticket_price ?? ''}
                  onChange={handleChange}
                  disabled={formData?.entry_type != '1'}
                />
                {formData?.entry_type === '1' && formErrors.ticket_price && <div className="invalid-feedback">{formErrors.ticket_price}</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label">Theme</label>
                <select
                  className="form-select"
                  name="theme"
                  value={formData.theme ?? ''}
                  onChange={handleChange}
                >
                  <option value="">Select Theme</option>
                  {
                    themes?.sort((a, b) => a?.theme?.localeCompare(b?.theme))?.map((m, i) => (
                      <option value={m?.id} key={i}>{m?.theme}</option>
                    ))
                  }
                </select>
              </div>
              {formData?.theme === "99" && <div className="col-md-6">
                <label className="form-label">Others</label>
                <input
                  type="text"
                  className="form-control"
                  name="theme_others"
                  value={formData.theme_others ?? ''}
                  onChange={handleChange}
                />
              </div>}
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Agenda Document</label>
                <input
                  type="file"
                  className="form-control p-2"
                  name="agenda_document"
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Number of Participants</label>
                <input
                  type="number"
                  className="form-control p-2"
                  name="number_of_participants"
                  value={formData.number_of_participants ?? ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="project_id" className="form-label">
                  Project
                </label>
                <select
                  name="project_id"
                  id="project_id"
                  value={formData.project_id ?? ''}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value=''>Select Project</option>
                  {projectData?.length &&
                    projectData.map((m, i) => (
                      <option key={i} value={m.id}>
                        {m.title}
                      </option>
                    ))
                  }
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Email Signature</label>
                <textarea
                  className={`form-control`}
                  name="email_sign"
                  rows="3"
                  value={formData.email_sign ?? ''}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label required">Prerequisites</label>
              <TipTapEditor
                value={formData.prerequistes ?? ''}
                onChange={(content) => {
                  setFormData({ ...formData, prerequistes: content });
                  if (formErrors.prerequistes) {
                    setFormErrors((prev) => ({
                      ...prev,
                      prerequistes: "",
                    }));
                  }
                }}
                isGrayMode={false}
              />
              {formErrors.prerequistes && (
                <div className="text-danger mt-1">{formErrors.prerequistes}</div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Speakers</label>
              {formData.organization_details.map((speaker, index) => (
                <div key={index} className="row mb-3">
                  <div className='col-md-5'>
                    <input
                      type="text"
                      className={`form-control ${(speaker.organization && !speaker.name) ? 'required' : ''} ${(formErrors.speakers && speaker.organization) ? 'is-invalid' : ''}`}
                      placeholder="Speaker Name"
                      value={speaker.name}
                      onChange={(e) => handleSpeakerChange(index, 'name', e.target.value)}
                    />
                    {speaker.organization && formErrors.speakers && <div className="invalid-feedback">{formErrors.speakers}</div>}
                  </div>
                  <div className='col-md-5'>
                    <input
                      type="text"
                      className={`form-control ${speaker.name ? 'required' : ''} ${(speaker.name && formErrors.organization) ? 'is-invalid' : ''}`}
                      placeholder="Organization"
                      value={speaker.organization}
                      onChange={(e) => handleSpeakerChange(index, 'organization', e.target.value)}
                    />
                    {speaker.name && formErrors.organization && <div className="invalid-feedback">{formErrors.organization}</div>}
                  </div>
                  <div className='col-md-2'>
                    {index === 0 ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary me-2"
                        onClick={addSpeaker}
                      >
                        Add Speaker
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => removeSpeaker(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-warning">
                <i className="fas fa-save"></i> {isEdit ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventForm;